import { Plural, Trans } from '@lingui/macro'
import Color from 'color'
import React from 'react'
import styled from 'styled-components/macro'

import { PrimaryButton } from '../../common/Buttons'
import { ShopAddress } from '../../common/ShopAddress'
import { FPickupBranchFragment } from '../../graph/generated'
import { useDelayedOrder } from '../../hooks/useDelayedOrder'
import { IShopStatus, useShopStatus } from '../../hooks/useShopStatus'

interface IProps {
  branch: FPickupBranchFragment
  onConfirm(): void
  onClose(): void
}

const StyledPickupMapBranch = styled.div`
  font-size: 1rem;
  display: flex;
  flex-direction: column;
  background-color: white;
  max-width: 18rem;
  min-width: 13rem;
  border-radius: 8px;
  box-shadow: 0 2px 7px 1px rgba(0, 0, 0, 0.3);
  position: relative;
  /* left: -50%; */
  top: 5rem;

  > header {
    flex: 0 0 auto;
    background-color: ${({ theme }) => theme.primary};
    color: ${({ theme }) => theme.tertiaryText};
    padding: 0.4rem;
    padding-right: 1.3rem;
    border-radius: 8px 8px 0 0;
  }

  > p {
    flex: 1 1 0;
    margin: 0.5rem;
  }

  > button {
    flex: 1 0 auto;
    margin: 0 0.3rem 0.3rem 0.3rem;
    font-size: 1rem;
    line-height: 1.5rem;
    text-transform: uppercase;
    font-variant-caps: inherit;
  }

  .close {
    position: absolute;
    right: 0;
    top: 0;
    padding: 0.5rem;
    font-size: 0.9rem;
    cursor: pointer;
    width: 2rem;

    > .icon {
      width: 100%;
      height: 100%;
      stroke: currentColor;
    }

    &:hover {
      border-radius: 0 8px 0 0;
      background-color: ${({ theme }) =>
        Color(theme.primary)
          .lighten(0.5)
          .toString()};
    }
  }
`

export const PickupMapBranch: React.FC<IProps> = ({
  branch,
  onConfirm,
  onClose,
}) => {
  const { canBeDelayed, isEnabled, shopIsClosed } = useDelayedOrder({
    forBranchId: branch.id,
  })
  const shopStatus = useShopStatus({
    status: branch,
  })

  const userCanPreOrder = shopIsClosed && canBeDelayed() && isEnabled

  return (
    <StyledPickupMapBranch data-testid="PickupMapBranch">
      <header>
        {branch.name}
        <div className="close" onClick={onClose}>
          <svg viewBox="0 0 10 10" className="icon">
            <path d="M1,1 9,9 M1,9 9,1" />
          </svg>
        </div>
      </header>
      <p>
        <ShopAddress address={branch.address} />
      </p>
      <PrimaryButton
        color="primary"
        data-testid="submitPickupBranch"
        disabled={!branch.pickupEnabled}
        onClick={onConfirm}
      >
        {branch.pickupEnabled ? (
          getButtonText(shopStatus, userCanPreOrder)
        ) : (
          <Trans>Delivery only</Trans>
        )}
      </PrimaryButton>
    </StyledPickupMapBranch>
  )
}

function getButtonText(
  { decision, openingIn, closingIn }: IShopStatus,
  canPreOrder: boolean,
) {
  if (canPreOrder) {
    return <Trans>Pre-order now</Trans>
  }

  switch (decision) {
    case 'Closed':
      return <Trans>Closed</Trans>
    case 'ClosingSoon':
      return (
        <Plural
          value={closingIn}
          one="Closing in 1 minute"
          other="Closing in # minutes"
        />
      )
    case 'OpeningSoon':
      return (
        <Plural
          value={openingIn}
          one="Opening in 1 minute"
          other="Opening in # minutes"
        />
      )
    default:
      return <Trans>Pickup here</Trans>
  }
}
